// Rechnungen für die Bestell-App – öffentliche API v1 (docs/integration/bestell-app.md §3.9): Liste und
// Einzelansicht mit Druckmodell, ausschließlich Belege (Rechnung, Rechnungskorrektur) des genannten Kunden. Ein
// unbekannter Kunde und der Beleg eines anderen Kunden antworten gleich (404).
import { defineContract } from '@shared/contract'
import { zDocumentPrintModel } from '@shared/document'
import { zId } from '@shared/ids'
import { zListPage } from '@shared/list'
import { z } from 'zod'

const zText = (max: number) => z.string().trim().max(max)

export const CHANNEL_INVOICE_STATUSES = ['open', 'partially_paid', 'paid', 'overdue', 'cancelled'] as const
export const CHANNEL_INVOICE_STATUS_LABELS: Record<(typeof CHANNEL_INVOICE_STATUSES)[number], string> = {
  open: 'Offen',
  partially_paid: 'Teilweise bezahlt',
  paid: 'Bezahlt',
  overdue: 'Überfällig',
  cancelled: 'Storniert',
}

export const zChannelInvoiceRow = z.object({
  id: zId,
  number: z.string(),
  type: z.enum(['invoice', 'credit_note']),
  documentDate: z.string(),
  dueDate: z.string().nullable(),
  priceMode: z.enum(['gross', 'net']),
  grossCents: z.int(),
  /** noch offen (Rechnungskorrektur: negativ) */
  openCents: z.int(),
  status: z.enum(CHANNEL_INVOICE_STATUSES),
  statusLabel: z.string(),
  deliveryNumbers: z.array(z.string()),
  /** Kennungen der Bestell-App zu den berechneten Lieferungen */
  externalOrderIds: z.array(z.string()),
})

export const financeChannelInvoices = defineContract({
  name: 'finance.channel.invoices',
  title: 'Rechnungen eines Kunden für einen Vertriebskanal lesen',
  description: 'Gebuchte Rechnungen und Rechnungskorrekturen genau eines Kunden, neueste zuerst – mit Zahlungsstand.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Liefert zur Kundennummer die gebuchten Rechnungen und ' +
      'Rechnungskorrekturen, neueste zuerst, seitenweise: Nummer, Datum, Fälligkeit, Betrag, offener Betrag, Stand ' +
      '(offen, teilweise bezahlt, bezahlt, überfällig, storniert), Lieferscheine und Bestellungen der App. Nur Belege ' +
      'dieses Kunden; ein unbekannter Kunde ergibt 404.',
    keywords: ['Rechnungen', 'Bestell-App', 'Schnittstelle', 'Kundenkonto'],
  },
  input: z.strictObject({ customerNumber: zText(40).min(1, { error: 'Die Kundennummer fehlt.' }), page: zListPage }),
  output: z.object({ rows: z.array(zChannelInvoiceRow), nextCursor: z.string().nullable() }),
  permission: 'finance.invoice.read',
  risk: 'read',
  scope: 'any',
  publicApi: true,
})

export const financeChannelInvoice = defineContract({
  name: 'finance.channel.invoice',
  title: 'Rechnung eines Kunden für einen Vertriebskanal lesen',
  description: 'Eine gebuchte Rechnung oder Rechnungskorrektur genau dieses Kunden mit Druckansicht.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Liefert zu Kundennummer und Belegnummer die Rechnung bzw. ' +
      'Rechnungskorrektur mit Zahlungsstand und dem Druckmodell (Absender, Empfänger, Positionen, Zuschläge, Steuern, ' +
      'Summen, Zahlung). Gehört der Beleg einem anderen Kunden, ist die Antwort dieselbe wie für eine unbekannte ' +
      'Nummer (404).',
    keywords: ['Rechnung', 'Druck', 'Bestell-App', 'Schnittstelle'],
  },
  input: z.strictObject({
    customerNumber: zText(40).min(1, { error: 'Die Kundennummer fehlt.' }),
    number: zText(40).min(1, { error: 'Die Belegnummer fehlt.' }),
  }),
  output: zChannelInvoiceRow.extend({ print: zDocumentPrintModel }),
  permission: 'finance.invoice.read',
  risk: 'read',
  scope: 'any',
  publicApi: true,
})
